// src/components/BillList.tsx
import { useState, useEffect, memo, useCallback, CSSProperties } from 'react';
import {
	DragDropContext,
	Draggable,
	Droppable,
	DropResult,
	DraggableProvided,
} from '@hello-pangea/dnd';
import {
	Paper,
	Text,
	Stack,
	Group,
	Box,
	ActionIcon,
	useMantineColorScheme,
	useMantineTheme,
	ScrollArea,
	Divider,
} from '@mantine/core';
import {
	IconReceipt2,
	IconPencil,
	IconGripVertical,
} from '@tabler/icons-react';
import { useListState } from '@mantine/hooks';
import { motion, AnimatePresence } from 'framer-motion';
import CustomSwitch from './CustomSwitch';
import EditBillModal from './EditBillModal';
import BillItemErrorBoundary from './BillItemErrorBoundary';
import { isDateOverdue, formatDateForDisplay } from '../utils/dateUtils';
import { Bill } from '../types';

interface BillItemProps {
	bill: Bill;
	onTogglePaid: (billId: string) => void;
	onDelete: (billId: string) => void;
	onUpdate: (bill: Bill) => void;
	provided: DraggableProvided;
	isDragging: boolean;
}

const BillItem = memo(function BillItem({
	bill,
	onTogglePaid,
	onDelete,
	onUpdate,
	provided,
	isDragging,
}: BillItemProps) {
	const [showEditModal, setShowEditModal] = useState(false);
	const [editHovered, setEditHovered] = useState(false);
	const { colorScheme } = useMantineColorScheme();
	const theme = useMantineTheme();
	const isDark = colorScheme === 'dark';

	const isOverdue = !bill.paid && isDateOverdue(bill.dueDate);

	const dueDate = formatDateForDisplay(bill.dueDate, 'M/D');

	const paperStyle: CSSProperties = {
		backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
		transition: 'box-shadow 0.2s ease, transform 0.2s ease',
		position: 'relative',
		...(isDragging && {
			boxShadow: theme.shadows.md,
		}),
	};

	const nameStyle: CSSProperties = {
		textDecoration: bill.paid ? 'line-through' : 'none',
		color: bill.paid
			? isDark
				? theme.colors.dark[2]
				: theme.colors.gray[5]
			: isDark
			? theme.white
			: theme.black,
		letterSpacing: '-0.03em',
		lineHeight: 1.2,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		flex: 1,
	};

	const amountStyle: CSSProperties = {
		letterSpacing: '-0.03em',
		lineHeight: 1.2,
	};

	return (
		<>
			<Paper
				p='md'
				radius='md'
				withBorder
				ref={provided.innerRef}
				{...provided.draggableProps}
				style={{ ...paperStyle, ...provided.draggableProps.style }}
			>
				<AnimatePresence>
					{isDragging && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							style={{
								position: 'absolute',
								top: -2,
								left: -2,
								right: -2,
								bottom: -2,
								border: '2px dashed var(--mantine-color-blue-6)',
								borderRadius:
									'calc(var(--mantine-radius-md) + 2px)',
								pointerEvents: 'none',
							}}
						/>
					)}
				</AnimatePresence>

				<Stack gap='sm'>
					<Group justify='space-between' wrap='nowrap'>
						<Group gap='sm' align='center'>
							<div
								{...provided.dragHandleProps}
								style={{ cursor: 'grab' }}
							>
								<IconGripVertical size={18} stroke={1.5} />
							</div>
							<Text
								size='lg'
								fw={600}
								style={nameStyle}
								title={bill.name}
							>
								{bill.name.length >
								(window.innerWidth <= 375 ? 12 : 24)
									? `${bill.name.slice(
											0,
											window.innerWidth <= 375 ? 12 : 24
									  )}...`
									: bill.name}
							</Text>
						</Group>
						<ActionIcon
							variant='subtle'
							color={editHovered ? 'blue' : 'gray'}
							size='lg'
							onClick={() => setShowEditModal(true)}
							onMouseEnter={() => setEditHovered(true)}
							onMouseLeave={() => setEditHovered(false)}
							style={{ flexShrink: 0 }}
						>
							<IconPencil size={24} />
						</ActionIcon>
					</Group>

					<Group
						justify='space-between'
						wrap='nowrap'
						align='center'
					>
						<Text
							size='xl'
							fw={700}
							c={bill.paid ? 'teal' : 'blue'}
							style={amountStyle}
						>
							${bill.amount.toFixed(2)}
						</Text>
						<Box pr={16}>
							<CustomSwitch
								checked={bill.paid}
								onChange={() => onTogglePaid(bill.id)}
								label={dueDate}
								isOverdue={isOverdue}
							/>
						</Box>
					</Group>
				</Stack>
			</Paper>

			<EditBillModal
				opened={showEditModal}
				onClose={() => setShowEditModal(false)}
				bill={bill}
				onUpdate={onUpdate}
				onDelete={onDelete}
			/>
		</>
	);
});

const EmptyState = () => (
	<Box p='xl' style={{ textAlign: 'center' }}>
		<Stack align='center' gap='sm'>
			<IconReceipt2
				size={48}
				style={{
					color: '#4F46E5',
					opacity: 0.5,
				}}
			/>
			<Box style={{ textAlign: 'center' }}>
				<Text size='lg' fw={500} c='dimmed'>
					No Bills Yet
				</Text>
				<Text size='sm' c='dimmed'>
					Add your first bill using the form above
				</Text>
			</Box>
		</Stack>
	</Box>
);

interface BillListProps {
	bills: Bill[];
	onTogglePaid: (billId: string) => void;
	onDelete: (billId: string) => void;
	onUpdate: (bill: Bill) => void;
	onReorder: (bills: Bill[]) => void;
}

const BillList = ({
	bills,
	onTogglePaid,
	onDelete,
	onUpdate,
	onReorder,
}: BillListProps) => {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === 'dark';
	const [state, handlers] = useListState(bills);

	useEffect(() => {
		handlers.setState(bills);
	}, [bills, handlers]);

	const handleDragEnd = useCallback(
		({ destination, source }: DropResult) => {
			if (!destination) return;

			// Manually compute the new order
			const newState = [...state];
			const [removed] = newState.splice(source.index, 1);
			newState.splice(destination.index, 0, removed);

			handlers.setState(newState);
			onReorder(newState);
		},
		[state, handlers, onReorder]
	);

	return (
		<Box
			style={{
				height: '100%',
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				padding: '0 0 24px 0',
			}}
		>
			<Divider
				my='md'
				size='sm'
				color={isDark ? 'dark.4' : 'gray.2'}
				style={{ marginTop: 0 }}
			/>
			<ScrollArea h='100%' type='scroll' scrollbarSize={0}>
				{bills.length === 0 ? (
					<EmptyState />
				) : (
					<DragDropContext onDragEnd={handleDragEnd}>
						<Droppable droppableId='dnd-list' direction='vertical'>
							{(provided) => (
								<Stack
									gap='md'
									{...provided.droppableProps}
									ref={provided.innerRef}
								>
									{state.map((bill, index) => (
										<Draggable
											key={bill.id}
											index={index}
											draggableId={bill.id}
										>
											{(provided, snapshot) => (
												<BillItemErrorBoundary>
													<BillItem
														bill={bill}
														onTogglePaid={onTogglePaid}
														onDelete={onDelete}
														onUpdate={onUpdate}
														provided={provided}
														isDragging={
															snapshot.isDragging
														}
													/>
												</BillItemErrorBoundary>
											)}
										</Draggable>
									))}
									{provided.placeholder}
								</Stack>
							)}
						</Droppable>
					</DragDropContext>
				)}
			</ScrollArea>
		</Box>
	);
};

export default BillList;
